import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { fmt } from '../../utils/format';
import { useApi } from '../../hooks/useApi';
import { Btn, Input, Select, Modal, ModalFooter, Table, KpiCard, Spinner, ErrorMsg, useToast, clickable } from '../ui';

// ---------------------------------------------------------------------------
// RealEstateBudgetTab — budget & development pro forma for one R/E deal detail.
//
// Hierarchy: Detail → Building → Suite. Development costs are entered in ONE
// consolidated box, each line targeting a building or a specific suite (basis
// $ / $/SF / % preserved). Each building runs the full phased levered IRR;
// each suite gets its own cost / NOI / yield-on-cost (suite cost = its own
// lines + its rentable-SF share of the building-wide, untagged lines).
//
// Percent / rate / cap INPUTS are whole-number percents; server-computed
// yields & IRR come back as fractions (fmt.pct); typed rates use pctWhole.
// ---------------------------------------------------------------------------

const CATEGORIES = ['Land', 'Site Cost', 'TI Allowance', 'Building Cost', 'Soft Costs', 'Contingency', 'Other'];
const METHODS = [
  { value: 'Amount', label: '$ (flat amount)' },
  { value: 'PerSF', label: '$ / SF' },
  { value: 'PerAcre', label: '$ / Ac' },
  { value: 'Percent', label: '% of a base' },
];
const SF_BASES = [
  { value: 'Gross', label: 'Gross SF' },
  { value: 'Rentable', label: 'Rentable SF' },
];
const COST_PCT_BASES = [
  { value: 'HardCost', label: 'Hard cost (building + site)' },
  { value: 'TotalCostExContingency', label: 'Total cost (ex-contingency)' },
  { value: 'Land', label: 'Land' },
];
const INCOME_PCT_BASES = [{ value: 'GrossPotentialIncome', label: 'Gross potential income' }];
const EXPENSE_PCT_BASES = [
  { value: 'EffectiveGrossIncome', label: 'Effective gross income' },
  { value: 'TotalCost', label: 'Total cost' },
];
const BASE_LABEL = Object.fromEntries(
  [...COST_PCT_BASES, ...INCOME_PCT_BASES, ...EXPENSE_PCT_BASES].map((o) => [o.value, o.label])
);

// display helpers
const n2 = (v) => (v == null || v === '' || isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const pctWhole = (v) => (v == null || v === '' || isNaN(Number(v)) ? '—' : `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`);
const mult = (v) => (v == null || isNaN(Number(v)) ? '—' : `${Number(v).toFixed(2)}×`);

const basisText = (l) => {
  if (l.method === 'PerSF') return `$${n2(l.inputValue)} /SF · ${l.sfBasis === 'Rentable' ? 'rentable' : 'gross'}`;
  if (l.method === 'PerAcre') return `$${n2(l.inputValue)} /ac`;
  if (l.method === 'Percent') return `${n2(l.inputValue)}% of ${BASE_LABEL[l.percentBaseKey] || 'base'}`;
  return 'Flat $';
};

// Shared-cost allocation method (how a detail-level cost is spread).
const ALLOC_METHODS = [
  { value: 'SF', label: 'By square footage' },
  { value: 'Cost', label: "By each building's cost" },
];
const ALLOC_BASIS_LABEL = { sf: 'by SF', cost: 'by building cost', equal: 'split equally', unallocated: 'not allocated' };

// ---------------------------------------------------------------------------

export const RealEstateBudgetTab = ({ dealId, dealDetailId }) => {
  const { toast } = useToast();
  const base = `deals/${dealId}/details/${dealDetailId}/budget`;
  const { data, loading, error, reload } = useApi(() => api.get(base), [dealDetailId]);

  const [busy, setBusy] = useState(false);
  const [buildingModal, setBuildingModal] = useState(null);
  const [assumeModal, setAssumeModal] = useState(null);
  const [costModal, setCostModal] = useState(null);     // { editing? , form } | null
  const [sharedModal, setSharedModal] = useState(null); // { editing? , form } | null
  const [incomeModal, setIncomeModal] = useState(null); // { editing? , form } | null
  const [suiteModal, setSuiteModal] = useState(null);   // { buildingId, editing?, form } | null
  const [finModal, setFinModal] = useState(null);
  const [subTab, setSubTab] = useState('Overview');     // Overview | Development Cost | Income

  if (loading) return <Spinner label="Loading budget…" />;
  if (error) return <ErrorMsg msg={error.message || 'Failed to load budget'} onRetry={reload} />;

  const assumptions = (data && data.assumptions) || {};
  const buildings = (data && data.buildings) || [];
  const sharedCosts = (data && data.sharedCosts) || [];
  const computed = (data && data.computed) || { buildings: [], detail: {} };
  // Server-computed spread of each shared cost across the buildings, by ID.
  const sharedComputedById = Object.fromEntries((computed.sharedCosts || []).map((s) => [s.sharedCostID, s]));
  // HOW THE EXIT IS VALUED — resolved on the SERVER (proforma.js resolveExitBasis)
  // and never re-derived here: the budget's stored choice, else the deal
  // detail's property type. `perAcreExit` still drives the LAND vocabulary
  // (acres instead of gross/rentable SF, "Marketing" instead of "Lease-up"),
  // which is a question about the asset rather than about the exit.
  const exitBasis = (data && data.exitBasis) || 'capRate';
  const perAcreExit = exitBasis === 'perAcre';
  const fixedExit = exitBasis === 'amount';
  // Offered only where migration 180 has been applied — a control whose save
  // would be refused is worse than one that is not offered.
  const supportsExitAmount = !!(data && data.supportsExitAmount);
  const det = computed.detail || {};
  const cmap = Object.fromEntries((computed.buildings || []).map((b) => [b.buildingID, b]));
  const buildingsById = Object.fromEntries(buildings.map((p) => [p.buildingID, p]));

  const suiteNameOf = (buildingId, suiteID) => {
    const p = buildingsById[buildingId];
    const s = p && (p.suites || []).find((x) => x.suiteID === suiteID);
    return s ? s.name : null;
  };
  const targetLabel = (l) => {
    const p = buildingsById[l.buildingID];
    const bn = p ? p.name : 'Building';
    const sn = l.suiteID ? suiteNameOf(l.buildingID, l.suiteID) : null;
    return sn ? `${bn} → ${sn}` : bn;
  };
  // Every REAL cost line across all buildings/suites (carries resolved `amount`).
  // Synthetic `isShared` lines (a shared cost's allocated share, injected by the
  // pro-forma engine) are excluded — shared costs get their own section below.
  const allCostLines = (computed.buildings || []).flatMap((b) => (b.costLines || []).filter((l) => !l.isShared));
  // Target options for the cost/income modals: each building + its suites.
  const targetOptions = buildings.flatMap((p) => [
    { value: `${p.buildingID}|`, label: `${p.name} — whole building` },
    ...(p.suites || []).map((s) => ({ value: `${p.buildingID}|${s.suiteID}`, label: `${p.name} → ${s.name}` })),
  ]);
  // Every income/expense (NOI) line across all buildings/suites.
  const allNoiLines = (computed.buildings || []).flatMap((b) => (b.noiLines || []));
  const totalIncome = allNoiLines.filter((l) => l.kind === 'Income').reduce((s, l) => s + (l.amount || 0), 0);
  const totalExpense = allNoiLines.filter((l) => l.kind === 'Expense').reduce((s, l) => s + (l.amount || 0), 0);

  // ── write helpers (reload after each so server-computed metrics stay fresh) ─
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveAssumptions = (form) => run(async () => {
    await api.put(`${base}/assumptions`, form);
    setAssumeModal(null);
  });
  const saveBuilding = (form, editingId) => run(async () => {
    if (editingId) await api.put(`${base}/buildings/${editingId}`, form);
    else await api.post(`${base}/buildings`, form);
    setBuildingModal(null);
  });
  const deleteBuilding = (p) => {
    if (!window.confirm(`Remove building "${p.name}" and all its suites, costs & income?`)) return;
    run(async () => { await api.del(`${base}/buildings/${p.buildingID}`); });
  };
  // Consolidated cost save — target (building|suite) is part of the form.
  const saveCost = (form, editing) => run(async () => {
    const [buildingId, suiteRaw] = String(form.target || '').split('|');
    const payload = {
      suiteID: suiteRaw || null,
      category: form.category, description: form.description,
      method: form.method, inputValue: form.inputValue,
      sfBasis: form.sfBasis, percentBaseKey: form.percentBaseKey,
    };
    if (editing) {
      if (editing.buildingID === buildingId) {
        await api.put(`${base}/buildings/${buildingId}/lines/${editing.lineItemID}`, payload);
      } else {
        // moved to a different building → recreate then remove the old line
        await api.post(`${base}/buildings/${buildingId}/lines`, payload);
        await api.del(`${base}/buildings/${editing.buildingID}/lines/${editing.lineItemID}`);
      }
    } else {
      await api.post(`${base}/buildings/${buildingId}/lines`, payload);
    }
    setCostModal(null);
  });
  const deleteCost = (l) => run(async () => { await api.del(`${base}/buildings/${l.buildingID}/lines/${l.lineItemID}`); });

  // Detail-level shared cost — spread across the buildings by the engine.
  const saveShared = (form, editing) => run(async () => {
    const payload = {
      category: form.category, description: form.description,
      amount: form.amount, allocationMethod: form.allocationMethod,
    };
    if (editing) await api.put(`${base}/shared-costs/${editing.sharedCostID}`, payload);
    else await api.post(`${base}/shared-costs`, payload);
    setSharedModal(null);
  });
  const deleteShared = (s) => {
    if (!window.confirm(`Remove shared cost "${s.description || s.category}"?`)) return;
    run(async () => { await api.del(`${base}/shared-costs/${s.sharedCostID}`); });
  };

  const saveSuite = (buildingId, form, editingId) => run(async () => {
    if (editingId) await api.put(`${base}/buildings/${buildingId}/suites/${editingId}`, form);
    else await api.post(`${base}/buildings/${buildingId}/suites`, form);
    setSuiteModal(null);
  });
  const deleteSuite = (buildingId, s) => {
    if (!window.confirm(`Remove suite "${s.name}"? Its cost & income lines move back to building-wide.`)) return;
    run(async () => { await api.del(`${base}/buildings/${buildingId}/suites/${s.suiteID}`); });
  };

  // Consolidated income/expense save — target (building|suite) is part of the form.
  const saveIncome = (form, editing) => run(async () => {
    const [buildingId, suiteRaw] = String(form.target || '').split('|');
    const payload = {
      suiteID: suiteRaw || null,
      kind: form.kind, label: form.label,
      method: form.method, inputValue: form.inputValue,
      sfBasis: form.sfBasis, percentBaseKey: form.percentBaseKey,
    };
    if (editing) {
      if (editing.buildingID === buildingId) {
        await api.put(`${base}/buildings/${buildingId}/noi/${editing.noiLineID}`, payload);
      } else {
        // moved to a different building → recreate then remove the old line
        await api.post(`${base}/buildings/${buildingId}/noi`, payload);
        await api.del(`${base}/buildings/${editing.buildingID}/noi/${editing.noiLineID}`);
      }
    } else {
      await api.post(`${base}/buildings/${buildingId}/noi`, payload);
    }
    setIncomeModal(null);
  });
  const deleteIncome = (l) => run(async () => { await api.del(`${base}/buildings/${l.buildingID}/noi/${l.noiLineID}`); });

  const saveFinancing = (bid, form) => run(async () => {
    await api.put(`${base}/buildings/${bid}/financing`, form);
    setFinModal(null);
  });

  const annual = Array.isArray(det.annualCashFlow) ? det.annualCashFlow : [];

  const openAssumptions = () => setAssumeModal({ form: {
    startDate: assumptions.startDate || '', holdMonths: assumptions.holdMonths ?? '',
    exitCapRate: assumptions.exitCapRate ?? '', exitPerAcre: assumptions.exitPerAcre ?? '',
    // The RESOLVED basis, not the stored one, so the modal opens showing what
    // the budget is actually doing rather than a blank where it inherited.
    exitBasis, exitAmount: assumptions.exitAmount ?? '',
    costOfSaleRate: assumptions.costOfSaleRate ?? '',
    debtMode: assumptions.debtMode || 'building',
    loanToCost: assumptions.loanToCost ?? '', loanAmount: assumptions.loanAmount ?? '',
    interestRate: assumptions.interestRate ?? '', amortYears: assumptions.amortYears ?? '', loanFeeRate: assumptions.loanFeeRate ?? '',
  } });
  const openNewCost = () => targetOptions.length
    ? setCostModal({ form: { ...EMPTY_COST, target: targetOptions[0].value } })
    : toast.error('Add a building first.');
  const openNewIncome = (kind) => targetOptions.length
    ? setIncomeModal({ form: { ...EMPTY_INCOME, kind, target: targetOptions[0].value } })
    : toast.error('Add a building first.');

  return (
    <div style={{ marginTop: 8 }}>
      {/* ── Mini sub-tabs ────────────────────────────────────────────────── */}
      <div style={{ display: 'inline-flex', gap: 2, padding: 3, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, marginBottom: 18 }}>
        {['Overview', 'Development Cost', 'Income'].map((t) => (
          <button key={t} onClick={() => setSubTab(t)} style={{
            border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
            padding: '6px 14px', borderRadius: 8, whiteSpace: 'nowrap',
            background: subTab === t ? B.surface : 'transparent',
            color: subTab === t ? B.navy : B.textMd,
            boxShadow: subTab === t ? B.shadowSm : 'none',
          }}>{t}</button>
        ))}
      </div>

      {/* ══ OVERVIEW ═════════════════════════════════════════════════════ */}
      {subTab === 'Overview' && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 12 }}>
          <KpiCard label="Total Development Cost" value={fmt.currency(det.totalCost)} sub={`${det.buildingCount || 0} building${det.buildingCount === 1 ? '' : 's'}`} />
          <KpiCard label="Equity Required" value={fmt.currency(det.totalEquity)} sub="after debt" />
          <KpiCard label="Levered IRR" value={det.leveredIRR != null ? fmt.pct(det.leveredIRR) : '—'} sub="phased, over hold" color={B.green} />
          <KpiCard label="Equity Multiple" value={mult(det.equityMultiple)} sub="levered" color={B.gold} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, padding: '12px 18px', marginBottom: 24, boxShadow: B.shadowSm }}>
          <Metric k="Stabilized NOI" v={fmt.currency(det.totalNOI)} color={B.green} />
          <Metric k="Blended Yield on Cost" v={det.blendedYieldOnCost != null ? fmt.pct(det.blendedYieldOnCost) : '—'} />
          <Metric k="Unlevered IRR" v={det.unleveredIRR != null ? fmt.pct(det.unleveredIRR) : '—'} />
          <Metric k="Total Debt" v={fmt.currency(det.totalDebt)} />
          {det.totalSharedCost > 0 && <Metric k="Shared Cost (spread)" v={fmt.currency(det.totalSharedCost)} />}
          <Metric k="Exit Value" v={fmt.currency(det.exitValue)} color={B.gold} />
        </div>

        <SectionBar title="Development Assumptions & Returns">
          <Btn size="sm" variant="ghost" onClick={openAssumptions}>Edit assumptions</Btn>
        </SectionBar>
        <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 18, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <Cell k="Analysis start" v={assumptions.startDate ? fmt.date(assumptions.startDate) : '—'} />
            {/* THE RESOLVED HORIZON, NOT THE RAW ASSUMPTION. An unset hold
                derives to the month the last building stabilizes, and a derived
                number must never read as an entered one — hence the sub-line.
                A hold shorter than stabilization clamps the engine's cost draws,
                so that is called out too rather than quietly producing a series
                nobody can reconcile with the construction window. */}
            <Cell
              k="Hold period"
              v={det.holdMonths ? `${det.holdMonths} mo` : '—'}
              sub={det.holdSource === 'stabilization'
                ? 'to stabilization — no hold entered'
                : (det.holdShortOfStabilization ? `ends before stabilization (${det.stabilizeMonths} mo)` : null)}
              subTone={det.holdShortOfStabilization ? B.red : undefined}
            />
            {fixedExit
              ? <Cell k="Exit amount" v={assumptions.exitAmount != null && assumptions.exitAmount !== '' ? fmt.currency(assumptions.exitAmount) : (det.exitValue ? fmt.currency(det.exitValue) : '—')} />
              : perAcreExit
                ? <Cell k="Exit $/Ac" v={assumptions.exitPerAcre != null && assumptions.exitPerAcre !== '' ? `${fmt.currency(assumptions.exitPerAcre)} /ac` : '—'} />
                : <Cell k="Exit cap rate" v={pctWhole(assumptions.exitCapRate)} />}
            <Cell k="Cost of sale" v={pctWhole(assumptions.costOfSaleRate)} />
          </div>
          {det.debtMode === 'detail' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, padding: '10px 18px', background: B.surfaceMuted, borderTop: `1px solid ${B.divider}` }}>
              <Metric k="Debt" v="Deal-detail (portfolio)" />
              <Metric k="Loan sizing" v={assumptions.loanToCost != null ? `${pctWhole(assumptions.loanToCost)} LTC` : fmt.currency(assumptions.loanAmount)} />
              <Metric k="Interest rate" v={pctWhole(assumptions.interestRate)} />
              <Metric k="Amortization" v={assumptions.amortYears ? `${assumptions.amortYears} yr` : 'Interest-only'} />
              <Metric k="Loan proceeds" v={fmt.currency(det.totalDebt)} />
              <Metric k="Annual debt service" v={fmt.currency(det.detailDebtService)} />
            </div>
          )}
          {annual.length > 0 && <CashFlowChart annual={annual} />}
        </div>

        <SectionBar title="Buildings & Analysis">
          <Btn size="sm" onClick={() => setBuildingModal({ form: EMPTY_BUILDING })}>+ Add Building</Btn>
        </SectionBar>
        {buildings.length === 0 && (
          <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '28px 0' }}>
            No buildings yet. Add a building to start the budget.
          </div>
        )}
        {buildings.map((p) => (
          <BuildingCard
            key={p.buildingID}
            building={p}
            c={cmap[p.buildingID] || {}}
            debtMode={det.debtMode || 'building'}
            perAcreExit={perAcreExit}
            onEdit={() => setBuildingModal({ editing: p.buildingID, form: buildingToForm(p) })}
            onRemove={() => deleteBuilding(p)}
            onAddSuite={() => setSuiteModal({ buildingId: p.buildingID, form: EMPTY_SUITE })}
            onEditSuite={(s) => setSuiteModal({ buildingId: p.buildingID, editing: s.suiteID, form: suiteToForm(s) })}
            onDeleteSuite={(s) => deleteSuite(p.buildingID, s)}
            onEditFinancing={() => setFinModal({ buildingId: p.buildingID, form: finToForm(p.financing) })}
          />
        ))}
      </>}

      {/* ══ DEVELOPMENT COST ═════════════════════════════════════════════ */}
      {subTab === 'Development Cost' && <>
        <SectionBar title="Development Costs — all buildings & suites">
          <Btn size="sm" onClick={openNewCost}>+ Add cost</Btn>
        </SectionBar>
        <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 24, padding: '14px 18px 18px' }}>
          {allCostLines.length === 0 ? (
            <Empty text="No cost lines yet. Add a building (Overview tab), then add costs here." />
          ) : (
            <Table
              columns={[
                { key: '_target', label: 'Building / Suite', render: (_, r) => targetLabel(r) },
                { key: 'category', label: 'Category' },
                { key: 'description', label: 'Description', render: (v) => v || '—' },
                { key: '_basis', label: 'Basis', render: (_, r) => basisText(r) },
                { key: 'amount', label: 'Amount', align: 'right', render: (v) => fmt.currency(v) },
                { key: '_a', label: '', render: (_, r) => rowActions(
                  () => setCostModal({ editing: r, form: costToForm(r) }),
                  () => deleteCost(r)) },
              ]}
              data={allCostLines}
            />
          )}
          <FootRow label="Total Building / Suite Cost" value={fmt.currency(det.totalDirectCost)} />
        </div>

        {/* Shared / detail-level costs — spread across the buildings. */}
        <SectionBar title="Shared costs — spread across all buildings">
          <Btn size="sm" onClick={() => setSharedModal({ form: EMPTY_SHARED })}>+ Add shared cost</Btn>
        </SectionBar>
        <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 16, padding: '14px 18px 18px' }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
            A cost not tied to any one building (e.g. detail-wide site work, entitlement, or a developer fee). It's split across the
            buildings <b>by square footage</b> — or by each building's own cost (excluding shared costs) when no SF is entered.
          </div>
          {sharedCosts.length === 0 ? (
            <Empty text="No shared costs. Add one to spread a detail-level cost across the buildings." />
          ) : (
            <Table
              columns={[
                { key: 'category', label: 'Category' },
                { key: 'description', label: 'Description', render: (v) => v || '—' },
                { key: '_alloc', label: 'Spread across buildings', render: (_, r) => <SharedSpread s={r} computed={sharedComputedById[r.sharedCostID]} /> },
                { key: 'amount', label: 'Amount', align: 'right', render: (v) => fmt.currency(v) },
                { key: '_a', label: '', render: (_, r) => rowActions(
                  () => setSharedModal({ editing: r, form: sharedToForm(r) }),
                  () => deleteShared(r)) },
              ]}
              data={sharedCosts}
            />
          )}
          {det.unallocatedSharedCost > 0 && (
            <div style={{ fontSize: 12, color: B.amber, marginTop: 10 }}>
              {fmt.currency(det.unallocatedSharedCost)} of shared cost isn't allocated — add a building for it to spread onto.
            </div>
          )}
          <FootRow label="Total Shared Cost" value={fmt.currency(det.totalSharedCost)} />
        </div>

        <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 24, padding: '10px 18px 14px' }}>
          <FootRow label="Total Development Cost" value={fmt.currency(det.totalCost)} bold />
        </div>
      </>}

      {/* ══ INCOME ═══════════════════════════════════════════════════════ */}
      {subTab === 'Income' && <>
        <SectionBar title="Income & NOI — all buildings & suites">
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn size="sm" onClick={() => openNewIncome('Income')}>+ Add income</Btn>
            <Btn size="sm" variant="ghost" onClick={() => openNewIncome('Expense')}>+ Add expense</Btn>
          </div>
        </SectionBar>
        <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 24, padding: '14px 18px 18px' }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
            Operating income (e.g. farm/ag rent, hunting lease) and operating expenses (taxes, insurance) → Net Operating Income.
          </div>
          {allNoiLines.length === 0 ? (
            <Empty text="No income or expense lines yet." />
          ) : (
            <Table
              columns={[
                { key: '_target', label: 'Building / Suite', render: (_, r) => targetLabel(r) },
                { key: 'kind', label: 'Type', render: (v) => <KindDot kind={v} /> },
                { key: 'label', label: 'Line' },
                { key: '_basis', label: 'Basis', render: (_, r) => basisText(r) },
                { key: 'amount', label: 'Annual', align: 'right', render: (v) => fmt.currency(v) },
                { key: '_a', label: '', render: (_, r) => rowActions(
                  () => setIncomeModal({ editing: r, form: incomeToForm(r) }),
                  () => deleteIncome(r)) },
              ]}
              data={allNoiLines}
            />
          )}
          <FootRow label="Total Income" value={fmt.currency(totalIncome)} />
          <FootRow label="Total Operating Expense" value={fmt.currency(totalExpense)} />
          <FootRow label="Net Operating Income" value={fmt.currency(det.totalNOI)} bold color={B.green} />
        </div>
      </>}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {assumeModal && (
        <AssumptionsModal form={assumeModal.form} perAcreExit={perAcreExit} supportsExitAmount={supportsExitAmount}
          busy={busy} onClose={() => setAssumeModal(null)} onSave={saveAssumptions} />
      )}
      {buildingModal && (
        <BuildingModal form={buildingModal.form} editing={buildingModal.editing} perAcreExit={perAcreExit} fixedExit={fixedExit} busy={busy}
          onClose={() => setBuildingModal(null)} onSave={(f) => saveBuilding(f, buildingModal.editing)} />
      )}
      {costModal && (
        <CostModal form={costModal.form} editing={costModal.editing} targetOptions={targetOptions} busy={busy}
          onClose={() => setCostModal(null)} onSave={(f) => saveCost(f, costModal.editing)} />
      )}
      {sharedModal && (
        <SharedCostModal form={sharedModal.form} editing={sharedModal.editing} busy={busy}
          onClose={() => setSharedModal(null)} onSave={(f) => saveShared(f, sharedModal.editing)} />
      )}
      {suiteModal && (
        <SuiteModal form={suiteModal.form} editing={suiteModal.editing} busy={busy}
          onClose={() => setSuiteModal(null)} onSave={(f) => saveSuite(suiteModal.buildingId, f, suiteModal.editing)} />
      )}
      {incomeModal && (
        <IncomeModal form={incomeModal.form} editing={incomeModal.editing} targetOptions={targetOptions} busy={busy}
          onClose={() => setIncomeModal(null)} onSave={(f) => saveIncome(f, incomeModal.editing)} />
      )}
      {finModal && (
        <FinancingModal form={finModal.form} busy={busy}
          onClose={() => setFinModal(null)} onSave={(f) => saveFinancing(finModal.buildingId, f)} />
      )}
    </div>
  );
};

// ── small presentational helpers ────────────────────────────────────────────
const Metric = ({ k, v, color }) => (
  <div>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textLt, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 1, color: color || B.text, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
  </div>
);
const Cell = ({ k, v, sub, subTone }) => (
  <div style={{ padding: '12px 18px', borderRight: `1px solid ${B.divider}`, borderBottom: `1px solid ${B.divider}` }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textLt, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    {sub && <div style={{ fontSize: 10, color: subTone || B.textLt, marginTop: 2, fontStyle: 'italic' }}>{sub}</div>}
  </div>
);
const SectionBar = ({ title, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '24px 0 12px' }}>
    <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textMd, fontWeight: 700 }}>{title}</span>
    {children}
  </div>
);

const CashFlowChart = ({ annual }) => {
  const max = Math.max(1, ...annual.map((v) => Math.abs(v)));
  const lastIdx = annual.length - 1;
  return (
    <div style={{ padding: '16px 18px 18px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: 700, marginBottom: 14 }}>
        Levered cash flow — annual (equity out → operations → sale)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${annual.length}, 1fr)`, gap: 12, alignItems: 'end', height: 140, borderBottom: `1px solid ${B.border}` }}>
        {annual.map((v, i) => {
          const neg = v < 0;
          const color = neg ? B.red : i === lastIdx ? B.green : B.tealDk;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <div title={fmt.currency(v)} style={{ width: '55%', height: `${(Math.abs(v) / max) * 100}%`, minHeight: v === 0 ? 0 : 2, background: color, borderRadius: neg ? '0 0 4px 4px' : '4px 4px 0 0' }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${annual.length}, 1fr)`, gap: 12, marginTop: 6 }}>
        {annual.map((v, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 10, color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>
            <div>Yr {i}</div>
            <div>{fmt.currency(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const rowActions = (onEdit, onDelete) => (
  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
    <Btn size="sm" variant="ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={onEdit}>Edit</Btn>
    <Btn size="sm" variant="danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={onDelete}>Remove</Btn>
  </div>
);

// ── one building (analysis-focused, collapsible) ────────────────────────────
const BuildingCard = ({ building: p, c, debtMode, perAcreExit, onEdit, onRemove, onAddSuite, onEditSuite, onDeleteSuite, onEditFinancing }) => {
  const [collapsed, setCollapsed] = useState(true);
  const costLines = c.costLines || [];
  const noiLines = c.noiLines || [];
  const suites = c.suites || [];
  const income = noiLines.filter((l) => l.kind === 'Income');
  const expense = noiLines.filter((l) => l.kind === 'Expense');
  const sf = [p.grossSF ? `Gross ${n2(p.grossSF)} SF` : null, p.rentableSF ? `Rentable ${n2(p.rentableSF)} SF` : null].filter(Boolean).join(' · ');
  const fin = p.financing || {};

  // read-only category breakdown of this building's cost
  const catTotals = CATEGORIES.map((cat) => ({ cat, amt: costLines.filter((l) => l.category === cat).reduce((s, l) => s + (l.amount || 0), 0) })).filter((x) => x.amt);

  return (
    <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, boxShadow: B.shadowMd, marginBottom: 18, overflow: 'hidden' }}>
      {/* header — click to expand/collapse */}
      <div {...clickable(() => setCollapsed((v) => !v))} aria-expanded={!collapsed}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', background: B.navy, color: '#fff', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: B.teal, transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform .15s' }}>▶</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{p.name}</div>
            {(p.description || sf) && <div style={{ fontSize: 11, color: B.teal, marginTop: 2 }}>{[p.description, sf].filter(Boolean).join(' · ')}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <Btn size="sm" variant="ghost" style={{ background: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.35)', padding: '4px 10px', fontSize: 11 }} onClick={onEdit}>Edit</Btn>
          <Btn size="sm" variant="ghost" style={{ background: 'rgba(255,255,255,.08)', color: '#fff', border: '1px solid rgba(255,255,255,.35)', padding: '4px 10px', fontSize: 11 }} onClick={onRemove}>Remove</Btn>
        </div>
      </div>

      {/* building metric strip — always visible */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, padding: '12px 18px', background: B.surfaceMuted, borderBottom: collapsed ? 'none' : `1px solid ${B.divider}` }}>
        <Metric k="Total Cost" v={fmt.currency(c.totalCost)} />
        {c.allocatedSharedCost > 0 && <Metric k="incl. shared" v={fmt.currency(c.allocatedSharedCost)} />}
        <Metric k="Stabilized NOI" v={fmt.currency(c.noi)} color={B.green} />
        <Metric k="Yield on Cost" v={c.yieldOnCost != null ? fmt.pct(c.yieldOnCost) : '—'} color={B.green} />
        {debtMode !== 'detail' && <Metric k="Equity" v={fmt.currency(c.equity)} />}
        {debtMode !== 'detail' && <Metric k="Levered IRR" v={c.leveredIRR != null ? fmt.pct(c.leveredIRR) : '—'} color={B.navy} />}
        {debtMode !== 'detail' && <Metric k="Equity Multiple" v={mult(c.equityMultiple)} color={B.gold} />}
        {collapsed && <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: B.textLt }}>{suites.length ? `${suites.length} suite${suites.length === 1 ? '' : 's'} · ` : ''}click to expand</div>}
      </div>

      {!collapsed && <>
      {/* suites — separate analysis per suite */}
      <div style={{ padding: '14px 18px 18px' }}>
        <SubHead title="Suites">
          <Btn size="sm" variant="ghost" onClick={onAddSuite}>+ Add Suite</Btn>
        </SubHead>
        {suites.length === 0 ? (
          <Empty text="No suites. Costs & income stay at the building level. Add a suite to analyze it separately." />
        ) : (
          <Table
            columns={[
              { key: 'name', label: 'Suite' },
              { key: 'rentableSF', label: 'Rentable SF', align: 'right', render: (v) => (v != null ? n2(v) : '—') },
              { key: 'ownCost', label: 'Own Cost', align: 'right', render: (v) => fmt.currency(v) },
              { key: 'allocatedCost', label: 'Allocated', align: 'right', render: (v) => fmt.currency(v) },
              { key: 'cost', label: 'Total Cost', align: 'right', render: (v) => fmt.currency(v) },
              { key: 'noi', label: 'NOI', align: 'right', render: (v) => fmt.currency(v) },
              { key: 'yieldOnCost', label: 'Yield', align: 'right', render: (v) => (v != null ? fmt.pct(v) : '—') },
              { key: '_a', label: '', render: (_, r) => rowActions(
                () => onEditSuite((p.suites || []).find((s) => s.suiteID === r.suiteID) || r),
                () => onDeleteSuite(r)) },
            ]}
            data={suites}
          />
        )}
      </div>

      {/* NOI summary (read-only — income/expense entry lives in the Income tab) */}
      <div style={{ padding: '14px 18px 18px', borderTop: `1px solid ${B.divider}` }}>
        <SubHead title="Projected NOI" />
        <FootRow label="Total Income" value={fmt.currency(income.reduce((s, l) => s + (l.amount || 0), 0))} />
        <FootRow label="Total Operating Expense" value={fmt.currency(expense.reduce((s, l) => s + (l.amount || 0), 0))} />
        <FootRow label="Net Operating Income" value={fmt.currency(c.noi)} bold color={B.green} />
      </div>

      {/* cost summary (read-only — entry lives in the consolidated box) */}
      {catTotals.length > 0 && (
        <div style={{ padding: '14px 18px 18px', borderTop: `1px solid ${B.divider}` }}>
          <SubHead title="Cost by Category" />
          {catTotals.map((x) => (
            <div key={x.cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, color: B.textMd }}>
              <span>{x.cat}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt.currency(x.amt)}</span>
            </div>
          ))}
          <FootRow label="Total Cost" value={fmt.currency(c.totalCost)} bold />
        </div>
      )}

      {/* financing & timing */}
      <div style={{ padding: '14px 18px 18px', borderTop: `1px solid ${B.divider}` }}>
        <SubHead title={debtMode === 'detail' ? 'Timing' : 'Financing & Timing'}>
          {debtMode !== 'detail' && <Btn size="sm" variant="ghost" onClick={onEditFinancing}>Edit</Btn>}
        </SubHead>
        {debtMode === 'detail' && (
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10, fontStyle: 'italic' }}>
            Debt is handled at the deal-detail level — edit it under “Development Assumptions & Returns”.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', border: `1px solid ${B.divider}`, borderRadius: 8, overflow: 'hidden' }}>
          {debtMode !== 'detail' && <>
            <Cell k="Loan sizing" v={fin.loanToCost != null ? `${pctWhole(fin.loanToCost)} LTC` : fin.loanAmount != null ? fmt.currency(fin.loanAmount) : '—'} />
            <Cell k="Interest rate" v={pctWhole(fin.interestRate)} />
            <Cell k="Amortization" v={fin.amortYears ? `${fin.amortYears} yr` : 'Interest-only'} />
            <Cell k="Loan fee" v={pctWhole(fin.loanFeeRate)} />
            <Cell k="Loan proceeds" v={fmt.currency(c.loanProceeds)} />
            <Cell k="Annual debt service" v={fmt.currency(c.annualDebtService)} />
          </>}
          <Cell k="Construction" v={`Mo ${p.constructionStartMonth ?? 0} · ${p.constructionMonths ?? 0} mo`} />
          <Cell k={perAcreExit ? 'Marketing' : 'Lease-up'} v={`${p.leaseUpMonths ?? 0} mo`} />
        </div>
      </div>
      </>}
    </div>
  );
};

const SubHead = ({ title, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
    <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: 700 }}>{title}</span>
    {children}
  </div>
);
const Empty = ({ text }) => <div style={{ color: B.textLt, fontSize: 13, padding: '10px 0' }}>{text}</div>;
const KindDot = ({ kind }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: kind === 'Expense' ? B.amber : B.green }} />
    {kind}
  </span>
);
const FootRow = ({ label, value, bold, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px 0', borderTop: `1px solid ${B.border}`, marginTop: 2 }}>
    <span style={{ fontWeight: 700, fontSize: bold ? 14 : 13, color: B.textMd }}>{label}</span>
    <span style={{ fontWeight: 700, fontSize: bold ? 14 : 13, color: color || B.text, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

// How a shared cost's amount landed on each building (server-computed).
const SharedSpread = ({ s, computed }) => {
  const allocs = ((computed && computed.allocations) || []).filter((a) => a.amount);
  const basis = ALLOC_BASIS_LABEL[computed && computed.allocationBasis] || ALLOC_BASIS_LABEL[s.allocationMethod === 'Cost' ? 'cost' : 'sf'];
  if (!allocs.length) {
    return <span style={{ fontSize: 12, color: B.textLt }}>{basis === ALLOC_BASIS_LABEL.unallocated ? 'not allocated (no buildings)' : `—`}</span>;
  }
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: B.textLt, fontWeight: 600, marginBottom: 2 }}>{basis}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
        {allocs.map((a) => (
          <span key={a.buildingID} style={{ fontSize: 12, color: B.textMd, fontVariantNumeric: 'tabular-nums' }}>
            {a.name}: <b>{fmt.currency(a.amount)}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── forms / modals ───────────────────────────────────────────────────────────
const EMPTY_BUILDING = { name: '', description: '', grossSF: '', rentableSF: '', acres: '', targetCapRate: '', targetPerAcre: '', targetExitAmount: '', constructionStartMonth: '', constructionMonths: '', leaseUpMonths: '' };
const EMPTY_SUITE = { name: '', grossSF: '', rentableSF: '', acres: '' };
const EMPTY_COST = { target: '', category: 'Land', description: '', method: 'Amount', inputValue: '', sfBasis: 'Gross', percentBaseKey: 'HardCost' };
const EMPTY_INCOME = { target: '', kind: 'Income', label: '', method: 'Amount', inputValue: '', sfBasis: 'Rentable', percentBaseKey: 'GrossPotentialIncome' };
const EMPTY_SHARED = { category: 'Site Cost', description: '', amount: '', allocationMethod: 'SF' };

const buildingToForm = (p) => ({
  name: p.name || '', description: p.description || '',
  grossSF: p.grossSF ?? '', rentableSF: p.rentableSF ?? '', acres: p.acres ?? '',
  targetCapRate: p.targetCapRate ?? '', targetPerAcre: p.targetPerAcre ?? '',
  targetExitAmount: p.targetExitAmount ?? '',
  constructionStartMonth: p.constructionStartMonth ?? '', constructionMonths: p.constructionMonths ?? '', leaseUpMonths: p.leaseUpMonths ?? '',
});
const suiteToForm = (s) => ({ name: s.name || '', grossSF: s.grossSF ?? '', rentableSF: s.rentableSF ?? '', acres: s.acres ?? '' });
const costToForm = (l) => ({
  target: `${l.buildingID}|${l.suiteID || ''}`,
  category: l.category || 'Land', description: l.description || '', method: l.method || 'Amount',
  inputValue: l.inputValue ?? '', sfBasis: l.sfBasis || 'Gross', percentBaseKey: l.percentBaseKey || 'HardCost',
});
const incomeToForm = (l) => ({
  target: `${l.buildingID}|${l.suiteID || ''}`,
  kind: l.kind, label: l.label || '', method: l.method || 'Amount',
  inputValue: l.inputValue ?? '', sfBasis: l.sfBasis || 'Rentable',
  percentBaseKey: l.percentBaseKey || (l.kind === 'Income' ? 'GrossPotentialIncome' : 'EffectiveGrossIncome'),
});
const sharedToForm = (s) => ({
  category: s.category || 'Site Cost', description: s.description || '',
  amount: s.amount ?? '', allocationMethod: s.allocationMethod || 'SF',
});
const finToForm = (f) => ({
  loanToCost: f?.loanToCost ?? '', loanAmount: f?.loanAmount ?? '', interestRate: f?.interestRate ?? '',
  amortYears: f?.amortYears ?? '', loanFeeRate: f?.loanFeeRate ?? '',
});

const Grid = ({ cols = 2, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>{children}</div>
);

const BuildingModal = ({ form: initial, editing, perAcreExit, fixedExit, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={editing ? 'Edit Building' : 'Add Building'} onClose={onClose} width={620}>
      <Input label="Name" value={f.name} onChange={(v) => set('name', v)} required autoFocus />
      <Input label="Description" value={f.description} onChange={(v) => set('description', v)} placeholder="e.g. Single-tenant retail pad" />
      {perAcreExit ? (
        <Grid>
          <Input label="SF" type="number" value={f.rentableSF} onChange={(v) => set('rentableSF', v)} />
          <Input label="Acres" type="number" value={f.acres} onChange={(v) => set('acres', v)} placeholder="for $/ac lines" />
        </Grid>
      ) : (
        <Grid cols={3}>
          <Input label="Gross SF" type="number" value={f.grossSF} onChange={(v) => set('grossSF', v)} />
          <Input label="Rentable SF" type="number" value={f.rentableSF} onChange={(v) => set('rentableSF', v)} />
          <Input label="Acres" type="number" value={f.acres} onChange={(v) => set('acres', v)} placeholder="for $/ac lines" />
        </Grid>
      )}
      {fixedExit
        ? <Input label="Exit amount ($) — this building's price, if no detail exit amount" type="number" value={f.targetExitAmount} onChange={(v) => set('targetExitAmount', v)} placeholder="e.g. 6000000" />
        : perAcreExit
          ? <Input label="Target $/Ac — for value if no detail exit $/Ac" type="number" value={f.targetPerAcre} onChange={(v) => set('targetPerAcre', v)} placeholder="e.g. 6500" />
          : <Input label="Target cap rate (%) — for value if no detail exit cap" type="number" value={f.targetCapRate} onChange={(v) => set('targetCapRate', v)} placeholder="e.g. 6.25" />}
      <Grid cols={3}>
        <Input label="Constr. start (mo)" type="number" value={f.constructionStartMonth} onChange={(v) => set('constructionStartMonth', v)} placeholder="0" />
        <Input label="Constr. months" type="number" value={f.constructionMonths} onChange={(v) => set('constructionMonths', v)} placeholder="0" />
        <Input label={perAcreExit ? 'Marketing months' : 'Lease-up months'} type="number" value={f.leaseUpMonths} onChange={(v) => set('leaseUpMonths', v)} placeholder="0" />
      </Grid>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy} disabled={!f.name.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const SuiteModal = ({ form: initial, editing, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={editing ? 'Edit Suite' : 'Add Suite'} onClose={onClose} width={520}>
      <Input label="Suite name" value={f.name} onChange={(v) => set('name', v)} required autoFocus placeholder="e.g. Suite 100" />
      <Grid cols={3}>
        <Input label="Gross SF" type="number" value={f.grossSF} onChange={(v) => set('grossSF', v)} />
        <Input label="Rentable SF" type="number" value={f.rentableSF} onChange={(v) => set('rentableSF', v)} />
        <Input label="Acres" type="number" value={f.acres} onChange={(v) => set('acres', v)} placeholder="for $/ac lines" />
      </Grid>
      <div style={{ fontSize: 12, color: B.textMd, marginTop: 4 }}>
        Rentable SF drives each suite's share of building-wide costs.
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy} disabled={!f.name.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const AssumptionsModal = ({ form: initial, perAcreExit, supportsExitAmount, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const detail = f.debtMode === 'detail';
  const basis = f.exitBasis || (perAcreExit ? 'perAcre' : 'capRate');
  // 'amount' is offered only where the database can store it (migration 180).
  const basisOptions = [
    { value: 'capRate', label: 'Cap rate — capitalize the stabilized NOI' },
    { value: 'perAcre', label: '$ per acre — land value x acres' },
    ...(supportsExitAmount ? [{ value: 'amount', label: 'Fixed amount — a price you already know' }] : []),
  ];
  return (
    <Modal title="Development Assumptions" onClose={onClose} width={600}>
      <Grid>
        <Input label="Analysis start" type="date" value={f.startDate} onChange={(v) => set('startDate', v)} />
        <Input label="Hold period (months)" type="number" value={f.holdMonths} onChange={(v) => set('holdMonths', v)} placeholder="e.g. 60" />
      </Grid>
      <Select label="Exit valued by" value={basis} onChange={(v) => set('exitBasis', v)} options={basisOptions} />
      <Grid>
        {basis === 'amount'
          ? <Input label="Exit amount ($)" type="number" value={f.exitAmount} onChange={(v) => set('exitAmount', v)} placeholder="e.g. 9500000" />
          : basis === 'perAcre'
            ? <Input label="Exit $/Ac" type="number" value={f.exitPerAcre} onChange={(v) => set('exitPerAcre', v)} placeholder="e.g. 6500" />
            : <Input label="Exit cap rate (%)" type="number" value={f.exitCapRate} onChange={(v) => set('exitCapRate', v)} placeholder="e.g. 6.25" />}
        <Input label="Cost of sale (%)" type="number" value={f.costOfSaleRate} onChange={(v) => set('costOfSaleRate', v)} placeholder="e.g. 2" />
      </Grid>
      {basis === 'amount' && (
        <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10 }}>
          The GROSS sale price for the whole capital item — cost of sale is still
          deducted and the loan is still paid off, exactly as under the other two
          bases. Leave it blank to price each building on its own card instead;
          across several buildings this figure is split pro-rata by cost.
        </div>
      )}

      <div style={{ borderTop: `1px solid ${B.divider}`, margin: '6px 0 12px' }} />
      <Select label="Debt" value={f.debtMode || 'building'} onChange={(v) => set('debtMode', v)}
        options={[{ value: 'building', label: 'By building (each building has its own loan)' }, { value: 'detail', label: 'By deal detail (one loan on total cost)' }]} />
      {detail ? (
        <>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 8 }}>
            One loan sized on the detail's total cost. Per-building financing is ignored; the levered IRR is reported at the detail level.
          </div>
          <Grid>
            <Input label="Loan-to-cost (%)" type="number" value={f.loanToCost} onChange={(v) => set('loanToCost', v)} placeholder="e.g. 60" />
            <Input label="Loan amount ($)" type="number" value={f.loanAmount} onChange={(v) => set('loanAmount', v)} />
            <Input label="Interest rate (%)" type="number" value={f.interestRate} onChange={(v) => set('interestRate', v)} placeholder="e.g. 6.75" />
            <Input label="Amortization (years)" type="number" value={f.amortYears} onChange={(v) => set('amortYears', v)} placeholder="blank = interest-only" />
            <Input label="Loan fee (%)" type="number" value={f.loanFeeRate} onChange={(v) => set('loanFeeRate', v)} placeholder="e.g. 1" />
          </Grid>
        </>
      ) : (
        <div style={{ fontSize: 12, color: B.textMd }}>Each building's loan is set on its own card under “Financing & Timing.”</div>
      )}

      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const MethodFields = ({ f, set, pctBases }) => (
  <>
    <Grid>
      <Select label="Basis" value={f.method} onChange={(v) => set('method', v)} options={METHODS} />
      <Input label={f.method === 'Percent' ? 'Percent (%)' : f.method === 'PerSF' ? 'Rate ($/SF)' : f.method === 'PerAcre' ? 'Rate ($/Ac)' : 'Amount ($)'}
        type="number" value={f.inputValue} onChange={(v) => set('inputValue', v)} />
    </Grid>
    {f.method === 'PerSF' && <Select label="Per which SF?" value={f.sfBasis} onChange={(v) => set('sfBasis', v)} options={SF_BASES} />}
    {f.method === 'Percent' && <Select label="Percent of" value={f.percentBaseKey} onChange={(v) => set('percentBaseKey', v)} options={pctBases} />}
  </>
);

const CostModal = ({ form: initial, editing, targetOptions, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={editing ? 'Edit Cost Line' : 'Add Cost Line'} onClose={onClose} width={560}>
      <Select label="Building / suite" value={f.target} onChange={(v) => set('target', v)} options={targetOptions} />
      <Grid>
        <Select label="Category" value={f.category} onChange={(v) => set('category', v)} options={CATEGORIES} />
        <Input label="Description" value={f.description} onChange={(v) => set('description', v)} />
      </Grid>
      <MethodFields f={f} set={set} pctBases={COST_PCT_BASES} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy} disabled={!f.target}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const SharedCostModal = ({ form: initial, editing, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title={editing ? 'Edit Shared Cost' : 'Add Shared Cost'} onClose={onClose} width={560}>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12 }}>
        A detail-level cost spread across all buildings. <b>By square footage</b> splits it by each building's SF (gross,
        else rentable); if no building has SF entered it falls back to each building's own cost (excluding shared costs).
        <b> By each building's cost</b> always splits it by the buildings' own costs.
      </div>
      <Grid>
        <Select label="Category" value={f.category} onChange={(v) => set('category', v)} options={CATEGORIES} />
        <Input label="Amount ($)" type="number" value={f.amount} onChange={(v) => set('amount', v)} autoFocus />
      </Grid>
      <Input label="Description" value={f.description} onChange={(v) => set('description', v)} placeholder="e.g. Shared site work, developer fee" />
      <Select label="Spread across buildings" value={f.allocationMethod} onChange={(v) => set('allocationMethod', v)} options={ALLOC_METHODS} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const IncomeModal = ({ form: initial, editing, targetOptions, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pctBases = f.kind === 'Income' ? INCOME_PCT_BASES : EXPENSE_PCT_BASES;
  return (
    <Modal title={`${editing ? 'Edit' : 'Add'} ${f.kind}`} onClose={onClose} width={560}>
      <Select label="Building / suite" value={f.target} onChange={(v) => set('target', v)} options={targetOptions} />
      <Grid>
        <Select label="Type" value={f.kind} onChange={(v) => set('kind', v)} options={[{ value: 'Income', label: 'Income' }, { value: 'Expense', label: 'Expense' }]} />
        <Input label="Label" value={f.label} onChange={(v) => set('label', v)} required autoFocus placeholder={f.kind === 'Income' ? 'e.g. Farm rent' : 'e.g. Property taxes'} />
      </Grid>
      <MethodFields f={f} set={set} pctBases={pctBases} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy} disabled={!f.target || !f.label.trim()}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const FinancingModal = ({ form: initial, busy, onClose, onSave }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Modal title="Financing" onClose={onClose} width={560}>
      <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12 }}>
        Size the loan by <b>Loan-to-cost %</b> or an explicit <b>loan amount</b> (LTC wins if both set). Leave amortization blank for interest-only.
      </div>
      <Grid>
        <Input label="Loan-to-cost (%)" type="number" value={f.loanToCost} onChange={(v) => set('loanToCost', v)} placeholder="e.g. 60" />
        <Input label="Loan amount ($)" type="number" value={f.loanAmount} onChange={(v) => set('loanAmount', v)} />
        <Input label="Interest rate (%)" type="number" value={f.interestRate} onChange={(v) => set('interestRate', v)} placeholder="e.g. 6.75" />
        <Input label="Amortization (years)" type="number" value={f.amortYears} onChange={(v) => set('amortYears', v)} placeholder="blank = interest-only" />
        <Input label="Loan fee (%)" type="number" value={f.loanFeeRate} onChange={(v) => set('loanFeeRate', v)} placeholder="e.g. 1" />
      </Grid>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(f)} loading={busy}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

export default RealEstateBudgetTab;
